import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  TypesOfShippings,
  PaymentMethod,
  CouponConfigurationResponse,
  CustomFieldDetail,
  CartItem,
  EstablishmentCheckout,
  Address,
  Country,
  ShippingOption,
  EndOrderDetailDigitalPayment,
  CustomField,
  IndexedCustomField,
  hasCustomFieldPaymentRestriction,
  hasCustomFieldShippingRestriction,
  isGlobalCustomField,
  isCustomFieldVisible,
  isPaymentMethodAllowedForShippingOption,
  getActiveShippingOptions,
  TransferPayer,
} from '@pedix-workspace/utils';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { FormsModule, NgForm } from '@angular/forms';
import {
  ApiV1_AvailableIntegration,
  ApiV1_Category,
  ApiV1_ProductTag,
  ApiV1_Establishment,
  ApiV1_Subsidiary,
} from '@pedix-workspace/api-v1';
import { getPaymentMethodValue } from '@pedix-workspace/shared-models';
import { EndOrderForm } from './end-order-form.service';
import { CouponValidationInvalidPaymentMethodDialogComponent } from '../coupon-validation-invalid-payment-method-dialog/coupon-validation-invalid-payment-method-dialog.component';
import {
  ShippingCostResponse,
  requiresManualConfirmation,
  hasShippingOptions,
} from '@pedix-workspace/shared-delivery';
import { CouponValidationService } from '@pedix-workspace/pedixapp-core-services';
import { EndOrderTotals } from '../end-order-totals.service';
import { EndOrderCustomerFieldsComponent } from './end-order-customer-fields/end-order-customer-fields.component';
import { EndOrderCustomFieldsComponent } from './end-order-custom-fields/end-order-custom-fields.component';
import { EndOrderShippingOptionFieldsComponent } from './end-order-shipping-option-fields/end-order-shipping-option-fields.component';
import { EndOrderPaymentMethodsFieldsComponent } from './end-order-payment-methods-fields/end-order-payment-methods-fields.component';
import { EndOrderCouponFieldsComponent } from './end-order-coupon-fields/end-order-coupon-fields.component';
import { InputTextComponent } from '@pedix-workspace/angular-ui-form-reactive';
import { EndOrderBillingFieldsComponent } from './end-order-billing-fields/end-order-billing-fields.component';
import {
  ARCA_VAT_CONDITION_CONSUMIDOR_FINAL,
  BillingInformationArca,
} from '@pedix-workspace/integrations-arca';
import { SubsidiarySelectedEvent } from '../end-order.service';

@Component({
  selector: 'pxw-end-order-form',
  templateUrl: './end-order-form.component.html',
  styleUrls: ['./end-order-form.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    FormsModule,
    CouponValidationInvalidPaymentMethodDialogComponent,
    EndOrderCustomerFieldsComponent,
    EndOrderCustomFieldsComponent,
    EndOrderShippingOptionFieldsComponent,
    EndOrderPaymentMethodsFieldsComponent,
    EndOrderCouponFieldsComponent,
    EndOrderBillingFieldsComponent,
    InputTextComponent,
  ],
})
export class EndOrderFormComponent implements OnInit {
  private toastrService = inject(ToastrService);
  private t = inject(TranslocoService);
  private couponValidationService = inject(CouponValidationService);

  readonly form = viewChild.required<NgForm>('form');
  readonly endOrderCouponFieldsComponent = viewChild(EndOrderCouponFieldsComponent);
  readonly endOrderPaymentMethodsFieldsComponent = viewChild(EndOrderPaymentMethodsFieldsComponent);
  readonly endOrderShippingOptionFieldsComponent = viewChild(EndOrderShippingOptionFieldsComponent);
  readonly couponValidationInvalidPaymentMethodDialogComponent = viewChild.required(
    CouponValidationInvalidPaymentMethodDialogComponent,
  );

  readonly hasPendingAsyncOperations = computed(() => {
    const shippingFields = this.endOrderShippingOptionFieldsComponent();
    const paymentFields = this.endOrderPaymentMethodsFieldsComponent();
    const couponFields = this.endOrderCouponFieldsComponent();

    return (
      shippingFields?.calculatingShippingCost() ||
      shippingFields?.checkingShippingCoverage() ||
      paymentFields?.cvuLoading() ||
      couponFields?.validatingCoupon()
    );
  });

  readonly scrollTarget = input.required<ElementRef>();
  readonly endOrderFormId = input.required<string>();
  readonly apiUrl = input.required<string>();
  readonly checkoutData = input.required<EstablishmentCheckout>();
  readonly establishment = input.required<ApiV1_Establishment>();
  readonly endOrderTotals = input.required<EndOrderTotals>();
  readonly categories = input.required<ApiV1_Category[]>();
  readonly productTags = input.required<ApiV1_ProductTag[]>();
  readonly workerServicesUrl = input.required<string>();
  readonly isAdminMode = input.required<boolean>();
  readonly couponsEnabled = input.required<boolean>();

  readonly paymentMethods = input<PaymentMethod[] | null>(null);
  readonly availableLogisticIntegrations = input<ApiV1_AvailableIntegration[]>([]);
  readonly availableIntegrations = input<ApiV1_AvailableIntegration[]>([]);
  readonly isCartDisabled = input<boolean>(false);
  readonly cartItems = input<CartItem[]>([]);
  readonly shippingRestrictionHint = input<string | null>(null);
  readonly paymentRestrictionHint = input<string | null>(null);
  readonly endOrderForm = input<EndOrderForm>();
  readonly personalInformationFieldsRequired = input<boolean>(false);
  readonly displayHowMuchField = input<boolean>(false);
  readonly displayBillingFields = input<boolean>(false);
  readonly subsidiarySelector = input<boolean>(false);
  readonly subsidiaries = input<ApiV1_Subsidiary[]>([]);

  /** Emits when digital payment data is generated (e.g., from bank transfer) */
  readonly digitalPaymentChange = output<EndOrderDetailDigitalPayment | null>();
  readonly selectedInstallments = model<number | undefined>(undefined);

  readonly couponConfigurationSnapshot = model<CouponConfigurationResponse | null>(null);
  readonly shippingCostResponse = model<ShippingCostResponse | null>(null);

  readonly name = signal<string | null | undefined>(null);
  readonly phone = signal<string | null | undefined>(null);
  readonly email = signal<string | null | undefined>(null);
  readonly shippingOptionType = signal<TypesOfShippings | null>(null);
  readonly paymentMethod = signal<PaymentMethod | null>(null);
  readonly tableNumber = signal<string | null>(null);
  readonly howMuch = signal<number | null>(null);
  readonly address = signal<Address | null>(null);
  readonly customFieldDetailValues = signal<CustomFieldDetail['value'][]>([]);
  readonly couponCode = signal<string | null>(null);
  readonly shippingOption = signal<ShippingOption | null>(null);
  readonly requestBilling = signal<boolean>(false);
  readonly billingInformation = signal<BillingInformationArca | null>(null);
  readonly transferPayer = signal<TransferPayer | null>(null);

  readonly selectedShippingMethod = signal<null>(null);
  readonly isFormSubmitted = signal<boolean>(false);

  readonly selectedSubsidiaryId = computed(() => this.endOrderForm()?.selectedSubsidiaryId || null);

  readonly shouldDisplayShippingOptions = computed(
    () => getActiveShippingOptions(this.checkoutData()).length > 0,
  );

  readonly shouldDisplayParcelTrackingEmail = computed(() => {
    const shippingOption = this.shippingOption();
    const shippingCostResponse = this.shippingCostResponse();

    if (shippingOption?.type !== TypesOfShippings.PARCEL || !shippingCostResponse) {
      return false;
    }

    if (requiresManualConfirmation(shippingCostResponse)) {
      return false;
    }

    return hasShippingOptions(shippingCostResponse);
  });

  readonly enabledPaymentMethods = computed<PaymentMethod[]>(() => {
    const shippingOption = this.shippingOption();

    return (this.paymentMethods() || []).filter(paymentMethod =>
      isPaymentMethodAllowedForShippingOption(shippingOption, getPaymentMethodValue(paymentMethod)),
    );
  });
  readonly countryCodes = computed<Country[] | null>(() =>
    this.establishment().countryCode ? [this.establishment().countryCode || 'XX'] : null,
  );

  readonly indexedCustomFields = computed<IndexedCustomField[]>(() => {
    const customFields = this.checkoutData().customFields || [];
    return customFields.map((field, index) => ({ field, originalIndex: index }));
  });

  readonly globalCustomFields = computed(() =>
    this.indexedCustomFields().filter(({ field }) => isGlobalCustomField(field)),
  );

  readonly shippingOnlyConditionalFields = computed(() =>
    this.indexedCustomFields().filter(
      ({ field }) =>
        hasCustomFieldShippingRestriction(field) && !hasCustomFieldPaymentRestriction(field),
    ),
  );

  readonly paymentConditionalFields = computed(() =>
    this.indexedCustomFields().filter(({ field }) => hasCustomFieldPaymentRestriction(field)),
  );

  readonly visibleShippingOnlyConditionalFields = computed(() => {
    const shippingOption = this.shippingOption();

    return this.shippingOnlyConditionalFields().filter(({ field }) =>
      this.isShippingConditionMet(field, shippingOption),
    );
  });

  readonly visiblePaymentConditionalFields = computed(() => {
    const paymentMethod = this.paymentMethod();
    const shippingOption = this.shippingOption();

    return this.paymentConditionalFields().filter(({ field }) =>
      isCustomFieldVisible(
        field,
        shippingOption?.type ?? null,
        paymentMethod ? getPaymentMethodValue(paymentMethod) : null,
      ),
    );
  });

  private isShippingConditionMet(
    field: CustomField,
    shippingOption: ShippingOption | null,
  ): boolean {
    if (!hasCustomFieldShippingRestriction(field)) {
      return true;
    }
    return !!shippingOption && field.allowedShippingOptions!.includes(shippingOption.type);
  }

  readonly formSubmit = output<void>();
  readonly endOrderFormChange = output<EndOrderForm>();
  readonly subsidiarySelected = output<SubsidiarySelectedEvent>();
  readonly openSubsidiariesSelector = output<void>();

  // Food Bank Donation ------------------------------------------------
  enableFoodBankDonation = input<boolean>(false);

  bankFoodDonationChecked = signal<boolean>(false);
  bankFoodDonationMoreInformation = signal<boolean>(false);

  readonly foodBankDonationAmount = input<number>();

  // -------------------------------------------------------------------

  constructor() {
    // EndOrderForm
    effect(() => {
      this.endOrderFormChange.emit({
        name: this.name(),
        phone: this.phone(),
        email: this.email(),
        requestBilling: this.requestBilling(),
        billingInformation: this.billingInformation(),
        shippingOption: this.shippingOption(),
        paymentMethod: this.paymentMethod(),
        tableNumber: this.tableNumber(),
        howMuch: this.howMuch(),
        address: this.address(),
        customFieldDetailValues: this.customFieldDetailValues(),
        couponCode: this.couponCode(),
        bankFoodDonationChecked: this.bankFoodDonationChecked(),
        selectedSubsidiaryId: this.selectedSubsidiaryId(),
        transferPayer: this.transferPayer(),
      });
    });

    // Unselect payment method if it's disabled for shipping option
    effect(
      () => {
        const paymentMethod = this.paymentMethod();

        if (paymentMethod && !this.enabledPaymentMethods().includes(paymentMethod)) {
          this.paymentMethod.set(null);
        }
      },
      {
        allowSignalWrites: true,
      },
    );

    // Clear values of conditional custom fields when they become hidden
    effect(
      () => {
        const visibleShippingIndices = this.visibleShippingOnlyConditionalFields().map(
          ({ originalIndex }) => originalIndex,
        );
        const visiblePaymentIndices = this.visiblePaymentConditionalFields().map(
          ({ originalIndex }) => originalIndex,
        );
        const visibleIndices = new Set([...visibleShippingIndices, ...visiblePaymentIndices]);

        const shippingOnlyIndices = this.shippingOnlyConditionalFields().map(
          ({ originalIndex }) => originalIndex,
        );
        const paymentIndices = this.paymentConditionalFields().map(
          ({ originalIndex }) => originalIndex,
        );
        const conditionalIndices = [...shippingOnlyIndices, ...paymentIndices];

        const currentValues = this.customFieldDetailValues();
        let hasChanges = false;
        const newValues = [...currentValues];

        for (const index of conditionalIndices) {
          if (!visibleIndices.has(index) && currentValues[index] !== undefined) {
            newValues[index] = undefined;
            hasChanges = true;
          }
        }

        if (hasChanges) {
          this.customFieldDetailValues.set(newValues);
        }
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  async ngOnInit() {
    const endOrderForm = this.endOrderForm();

    if (endOrderForm) {
      this.name.set(endOrderForm.name);
      this.phone.set(endOrderForm.phone);

      this.requestBilling.set(endOrderForm.requestBilling || false);

      if (this.displayBillingFields()) {
        this.billingInformation.set(
          <BillingInformationArca>endOrderForm.billingInformation || {
            provider: 'arca',
            vatCondition: ARCA_VAT_CONDITION_CONSUMIDOR_FINAL, // TODO: esto deberia ser variable por provider
          },
        );
      } else {
        this.billingInformation.set(null);
      }

      // NOTE: we do this because we need to get object reference from the array
      const selectedPaymentMethodValue = endOrderForm.paymentMethod
        ? getPaymentMethodValue(endOrderForm.paymentMethod)
        : null;
      const paymentMethod = this.paymentMethods()?.find(
        pm =>
          !!selectedPaymentMethodValue && getPaymentMethodValue(pm) === selectedPaymentMethodValue,
      );
      if (paymentMethod) {
        this.paymentMethod.set(paymentMethod);
      }

      const shippingOptionType =
        endOrderForm.shippingOption?.type === TypesOfShippings.PARCEL
          ? TypesOfShippings.DELIVERY
          : endOrderForm.shippingOption?.type;

      if (shippingOptionType) {
        this.shippingOptionType.set(shippingOptionType);
      }

      this.tableNumber.set(endOrderForm.tableNumber || null);
      this.howMuch.set(endOrderForm.howMuch || null);
      this.address.set(endOrderForm.address || null);
      this.customFieldDetailValues.set(endOrderForm.customFieldDetailValues || []);
      this.couponCode.set(endOrderForm.couponCode || null);
      this.bankFoodDonationChecked.set(endOrderForm.bankFoodDonationChecked || false);
      this.email.set(endOrderForm.email || null);
      this.transferPayer.set(endOrderForm.transferPayer ?? null);
    }
  }

  onUpdatePaymentMethod(paymentMethod: PaymentMethod | null) {
    const previousPaymentMethod = this.paymentMethod();
    const couponConfigurationSnapshot = this.couponConfigurationSnapshot();
    const enabledPaymentMethods = this.enabledPaymentMethods();

    this.paymentMethod.set(paymentMethod);

    if (
      couponConfigurationSnapshot &&
      this.couponValidationService.checkCouponConditionPaymentMethods(
        enabledPaymentMethods,
        paymentMethod,
        couponConfigurationSnapshot.paymentMethods,
      ) !== true
    ) {
      const paymentMethodsForCoupon = this.couponValidationService.getValidPaymentMethods(
        couponConfigurationSnapshot.paymentMethods,
        enabledPaymentMethods,
      );

      this.couponValidationInvalidPaymentMethodDialogComponent()
        .open(paymentMethodsForCoupon)
        .then(() => {
          this.removeCoupon();
        })
        .catch(() => this.paymentMethod.set(previousPaymentMethod));
    }
  }

  protected async onSubmit($event: FormDataEvent) {
    $event.preventDefault();

    this.isFormSubmitted.set(true);

    if (!this.form().valid) {
      this.toastrService.error(this.t.translate('endOrder.formError'));
      this.scrollToFormError();
      return;
    }
    this.formSubmit.emit();
  }

  scrollToFormError() {
    setTimeout(() => {
      const firstError = this.scrollTarget().nativeElement.querySelector('.ui-input__error');
      const firstErrorWrapper = firstError?.closest('.ui-input') || firstError?.parentElement;

      if (!firstErrorWrapper) {
        return;
      }
      this.scrollTarget().nativeElement.scrollTop = firstErrorWrapper.offsetTop - 100;
    }, 0);
  }

  removeCoupon() {
    this.endOrderCouponFieldsComponent()?.removeCoupon();
  }

  onToggleBankFoodDonationMoreInformation() {
    this.bankFoodDonationMoreInformation.update(
      bankFoodDonationMoreInformation => !bankFoodDonationMoreInformation,
    );
  }

  /**
   * Confirms the bank transfer amount before order submission.
   * Only applicable for ChytaPay integration.
   */
  async confirmBankTransferAmount(): Promise<void> {
    await this.endOrderPaymentMethodsFieldsComponent()?.confirmBankTransferAmount();
  }
}
